import React, { useState, useEffect, useMemo } from 'react';
import type { Recipe } from '../types';
import { curriculum } from '../curriculumData';

interface RecipeModalProps {
  isOpen: boolean;
  onClose: () => void;
  recipe: Recipe;
  onAddPrinciple: (principle: string) => void;
  onUpdatePrinciple: (index: number, newText: string) => void;
  onDeletePrinciple: (index: number) => void;
  onAddGeneralTool: (tool: string) => void;
  onUpdateGeneralTool: (index: number, newText: string) => void;
  onDeleteGeneralTool: (index: number) => void;
  onAddSpecificTool: (path: string, tool: string) => void;
  onUpdateSpecificTool: (path: string, index: number, newText: string) => void;
  onDeleteSpecificTool: (path: string, index: number) => void;
}

const RecipeModal: React.FC<RecipeModalProps> = ({ 
  isOpen, onClose, recipe, 
  onAddPrinciple, onUpdatePrinciple, onDeletePrinciple,
  onAddGeneralTool, onUpdateGeneralTool, onDeleteGeneralTool,
  onAddSpecificTool, onUpdateSpecificTool, onDeleteSpecificTool
}) => {
  const [newPrinciple, setNewPrinciple] = useState('');
  const [editingPrinciple, setEditingPrinciple] = useState<{ index: number; text: string } | null>(null);
  
  const [newGeneralTool, setNewGeneralTool] = useState('');
  const [editingGeneralTool, setEditingGeneralTool] = useState<{ index: number; text: string } | null>(null);

  const [newSpecificTool, setNewSpecificTool] = useState('');
  const [editingSpecificTool, setEditingSpecificTool] = useState<{ index: number; text: string } | null>(null);

  // Curriculum state
  const [selectedSubject, setSelectedSubject] = useState<string>('');
  const [selectedMajorUnit, setSelectedMajorUnit] = useState<string>('');
  const [selectedSubUnit, setSelectedSubUnit] = useState<string>('');

  const majorUnits = useMemo(() => selectedSubject ? Object.keys(curriculum[selectedSubject]) : [], [selectedSubject]);
  const subUnits = useMemo(() => (selectedSubject && selectedMajorUnit) ? curriculum[selectedSubject][selectedMajorUnit] : [], [selectedSubject, selectedMajorUnit]);
  const currentPath = useMemo(() => {
    if (selectedSubject && selectedMajorUnit && selectedSubUnit) {
      return `${selectedSubject}.${selectedMajorUnit}.${selectedSubUnit}`;
    }
    return '';
  }, [selectedSubject, selectedMajorUnit, selectedSubUnit]);
  
  const specificToolsForCurrentPath = useMemo(() => recipe.specificTools[currentPath] || [], [recipe.specificTools, currentPath]);

  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  if (!isOpen) return null;

  const handleAddPrinciple = () => { if (newPrinciple.trim()) { onAddPrinciple(newPrinciple); setNewPrinciple(''); } };
  const handleUpdatePrinciple = () => { if (editingPrinciple) { onUpdatePrinciple(editingPrinciple.index, editingPrinciple.text); setEditingPrinciple(null); } };
  
  const handleAddGeneralTool = () => { if (newGeneralTool.trim()) { onAddGeneralTool(newGeneralTool); setNewGeneralTool(''); } };
  const handleUpdateGeneralTool = () => { if (editingGeneralTool) { onUpdateGeneralTool(editingGeneralTool.index, editingGeneralTool.text); setEditingGeneralTool(null); } };

  const handleAddSpecificTool = () => { if (newSpecificTool.trim() && currentPath) { onAddSpecificTool(currentPath, newSpecificTool); setNewSpecificTool(''); } };
  const handleUpdateSpecificTool = () => { if (editingSpecificTool && currentPath) { onUpdateSpecificTool(currentPath, editingSpecificTool.index, editingSpecificTool.text); setEditingSpecificTool(null); } };

  const selectClasses = "w-full bg-[#1A1D2D] border border-[#5F6B8E] rounded-md py-2 px-3 text-white focus:outline-none focus:ring-2 focus:ring-[#C0A062]";

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex justify-center items-center z-50 p-4" onClick={onClose}>
      <div className="bg-[#1A1D2D] text-[#F0EAD6] rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto p-8 relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 text-[#A9B1D6] hover:text-white text-3xl leading-none" aria-label="Close">&times;</button>
        <div className="space-y-12">
          <div>
            <h2 className="text-2xl font-bold text-[#C0A062] mb-2">비밀레시피</h2>
            <p className="text-[#A9B1D6]">출.정AI가 최고의 퀄리티의 문항을 만들 때 참고할 레시피를 관리합니다.</p>
          </div>

          <EditableList
            title="1. 출제원칙"
            description="반드시 지켜야 하는 원칙입니다. AI는 이 원칙을 엄격하게 준수합니다."
            items={recipe.principles}
            newItem={newPrinciple}
            setNewItem={setNewPrinciple}
            editingItem={editingPrinciple}
            setEditingItem={setEditingPrinciple}
            onAddItem={handleAddPrinciple}
            onUpdateItem={handleUpdatePrinciple}
            onDeleteItem={onDeletePrinciple}
          />
          
          <EditableList
            title="2. 일반 출제도구"
            description="모든 문제에 공통적으로 적용될 수 있는 도구입니다."
            items={recipe.generalTools}
            newItem={newGeneralTool}
            setNewItem={setNewGeneralTool}
            editingItem={editingGeneralTool}
            setEditingItem={setEditingGeneralTool}
            onAddItem={handleAddGeneralTool}
            onUpdateItem={handleUpdateGeneralTool}
            onDeleteItem={onDeleteGeneralTool}
          />
          
          <div className="bg-[#252A41] p-6 rounded-lg shadow-inner space-y-4">
            <h3 className="text-xl font-semibold text-[#F0EAD6]">3. 단원별 세부 출제도구</h3>
            <p className="text-sm text-[#A9B1D6]">선택된 단원의 문제를 생성할 때만 특별히 사용될 도구입니다.</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-2">
              <select value={selectedSubject} onChange={e => { setSelectedSubject(e.target.value); setSelectedMajorUnit(''); setSelectedSubUnit(''); }} className={selectClasses}>
                  <option value="">과목 선택...</option>
                  {Object.keys(curriculum).map(subject => <option key={subject} value={subject}>{subject}</option>)}
              </select>
              <select value={selectedMajorUnit} onChange={e => { setSelectedMajorUnit(e.target.value); setSelectedSubUnit(''); }} className={selectClasses} disabled={!selectedSubject}>
                  <option value="">대단원 선택...</option>
                  {majorUnits.map(unit => <option key={unit} value={unit}>{unit}</option>)}
              </select>
              <select value={selectedSubUnit} onChange={e => setSelectedSubUnit(e.target.value)} className={selectClasses} disabled={!selectedMajorUnit}>
                  <option value="">소단원 선택...</option>
                  {subUnits.map(unit => <option key={unit} value={unit}>{unit}</option>)}
              </select>
            </div>
            {currentPath && (
              <EditableList
                items={specificToolsForCurrentPath}
                newItem={newSpecificTool}
                setNewItem={setNewSpecificTool}
                editingItem={editingSpecificTool}
                setEditingItem={setEditingSpecificTool}
                onAddItem={handleAddSpecificTool}
                onUpdateItem={handleUpdateSpecificTool}
                onDeleteItem={(index) => onDeleteSpecificTool(currentPath, index)}
                placeholder="이 단원의 세부 도구를 입력하세요..."
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Reusable component for editable lists
interface EditableListProps {
  title?: string;
  description?: string;
  items: string[];
  newItem: string;
  setNewItem: (value: string) => void;
  editingItem: { index: number; text: string } | null;
  setEditingItem: (item: { index: number; text: string } | null) => void;
  onAddItem: () => void;
  onUpdateItem: () => void;
  onDeleteItem: (index: number) => void;
  placeholder?: string;
}

const EditableList: React.FC<EditableListProps> = ({
  title, description, items, newItem, setNewItem, editingItem, setEditingItem,
  onAddItem, onUpdateItem, onDeleteItem, placeholder
}) => (
  <div className={title ? "bg-[#252A41] p-6 rounded-lg shadow-inner space-y-4" : "mt-4"}>
    {title && <h3 className="text-xl font-semibold text-[#F0EAD6]">{title}</h3>}
    {description && <p className="text-sm text-[#A9B1D6]">{description}</p>}
    <div className="flex flex-col sm:flex-row gap-2 pt-2">
      <input
        type="text"
        value={newItem}
        onChange={(e) => setNewItem(e.target.value)}
        onKeyPress={(e) => e.key === 'Enter' && onAddItem()}
        placeholder={placeholder || "새 항목을 입력하세요..."}
        className="flex-grow bg-[#1A1D2D] border border-[#5F6B8E] rounded-md py-2 px-3 text-white focus:outline-none focus:ring-2 focus:ring-[#C0A062]"
      />
      <button onClick={onAddItem} className="w-full sm:w-auto bg-[#C0A062] hover:bg-[#d4b57a] text-[#1A1D2D] font-bold py-2 px-4 rounded-lg transition-colors duration-300">추가</button>
    </div>
    {items.length === 0 ? (
      <p className="text-[#5F6B8E] text-center pt-4">설정된 항목이 없습니다.</p>
    ) : (
      <ul className="space-y-3 pt-4">
        {items.map((item, index) => (
          <li key={index} className="bg-[#1A1D2D] p-3 rounded-md flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            {editingItem?.index === index ? (
              <input type="text" value={editingItem.text} onChange={(e) => setEditingItem({ ...editingItem, text: e.target.value })} onKeyPress={(e) => e.key === 'Enter' && onUpdateItem()} className="flex-grow bg-[#252A41] border border-[#C0A062] rounded-md py-1 px-2 text-white" />
            ) : (
              <p className="text-[#A9B1D6] flex-grow break-all"><span className="font-bold text-[#C0A062] mr-2">{index + 1}.</span>{item}</p>
            )}
            <div className="flex-shrink-0 flex gap-2 self-end sm:self-center">
              {editingItem?.index === index ? (
                <>
                  <button onClick={onUpdateItem} className="text-sm text-green-400 hover:text-green-300">저장</button>
                  <button onClick={() => setEditingItem(null)} className="text-sm text-gray-400 hover:text-gray-300">취소</button>
                </>
              ) : (
                <>
                  <button onClick={() => setEditingItem({ index, text: item })} className="text-sm text-[#A9B1D6] hover:text-white">수정</button>
                  <button onClick={() => onDeleteItem(index)} className="text-sm text-rose-400 hover:text-rose-300">삭제</button>
                </>
              )}
            </div>
          </li>
        ))}
      </ul>
    )}
  </div>
);

export default RecipeModal;